import { useCallback, useMemo } from 'react';

import { useCurrentuser } from '@uplift/contexts';
import { useL10n } from '@uplift/hooks';
import { isLicenseExpired, licenseAllowsStateExhibit, licenseRequiresCollaborativeAgreement } from '@uplift/pages';
import { CalendarDateService } from '@uplift/services';
import { useCollaboratePhysicianMatchesForProviderQuery, useCollaborativeStatesDetailsQuery, useProviderCollaborativeMatchRequestsQuery, useProviderExhibitSignaturesQuery, useProviderLicensesCollabAgreementsQuery, useProviderLicensesStatusQuery, useProviderSignedCollaborativeAgreementsQuery, useStatesQuery } from '@uplift/queries';

import { PracticeStateRow, PracticeStateStatus } from './components/PracticeStatesTable';
import { CollaborativeAgreementSigned, CollaborativeAuthorizationRequirement, CollaborativePhysicianMatch, CollaborativeStateDetails, License, LicenseCollaborativeAgreement, State } from '@uplift/api';

const deriveStatus = (
  collaborativeStateDetails: CollaborativeStateDetails,
  license: License,
  activeCollabAgreement: LicenseCollaborativeAgreement | undefined,
  signedCollaborativeMatch: CollaborativePhysicianMatch | undefined,
  signedCollaborativeAgreement: CollaborativeAgreementSigned | undefined,
  matchRequested: boolean,
): PracticeStateStatus => {
  if (signedCollaborativeMatch) {
    if (signedCollaborativeAgreement) {
      if (signedCollaborativeAgreement.counterSignedAt) {
        if (license.live) {
          return 'live';
        }
        return 'inReview';
      } else {
        return 'agreementCounterSignRequired';
      }
    } else {
      return 'agreementUnsigned';
    }
  }

  if (isLicenseExpired(license)) {
    return 'expired';
  }

  if (license.live) {
    return 'live';
  }

  // an existing rejected agreement always surfaces, even if the state details are unavailable
  if (activeCollabAgreement?.rejectedAt != null) {
    return 'rejected';
  }

  if (licenseRequiresCollaborativeAgreement(license, collaborativeStateDetails)) {
    if (!activeCollabAgreement) {
      return matchRequested ? 'inProgress' : 'getStarted';
    }

    return 'inReview';
  }

  if (licenseAllowsStateExhibit(license, collaborativeStateDetails)) {
    return 'getStarted';
  }

  return 'goLive';
};

export const usePracticeStatesData = () => {
  const me = useCurrentuser();
  const l10n = useL10n();

  const getRequirementsSubtitle = useCallback((status: PracticeStateStatus, hasSignedExhibit: boolean, authorizationRequirement: CollaborativeAuthorizationRequirement): string | undefined => {
    if (status === 'live' || status === 'expired') {
      return undefined;
    }

    const collaborativeAgreementSubtitleByStatus: Partial<Record<PracticeStateStatus, string>> = {
      getStarted: l10n.practiceStates.collaborativeAgreement.required,
      inProgress: l10n.practiceStates.collaborativeAgreement.inProgress,
      inReview: l10n.practiceStates.collaborativeAgreement.inReview,
      rejected: l10n.practiceStates.collaborativeAgreement.rejected,
      agreementUnsigned: l10n.practiceStates.collaborativeAgreement.unsigned,
      agreementCounterSignRequired: l10n.practiceStates.collaborativeAgreement.awaitingCountersignature,
    };

    const collaborativeAgreementLabel = collaborativeAgreementSubtitleByStatus[status] ?? l10n.practiceStates.collaborativeAgreement.required;
    const stateExhibitLabel = hasSignedExhibit ? l10n.practiceStates.stateExhibit.signed : l10n.practiceStates.stateExhibit.required;
    const joiner = authorizationRequirement === 'both' ? '·' : 'or';

    return `${collaborativeAgreementLabel} ${joiner} ${stateExhibitLabel}`;
  }, [ l10n ]);

  const { data: licenses, isLoading: licensesLoading } = useProviderLicensesStatusQuery(me?.id);
  const { data: states = [], isLoading: statesLoading } = useStatesQuery();
  const { data: collaborativeAgreementsById, isLoading: collabAgreementsLoading } = useProviderLicensesCollabAgreementsQuery(me?.id);
  const { data: matches = [], isLoading: matchesLoading } = useCollaboratePhysicianMatchesForProviderQuery(me.id);
  const { data: signedCollaborativeAgreementsByMatchId, isLoading: signedCollaborativeAgreementsLoading } = useProviderSignedCollaborativeAgreementsQuery(me?.id);
  const { data: matchRequestsByLicenseId } = useProviderCollaborativeMatchRequestsQuery(me.id);
  const { data: collaborativeStateDetails = [] } = useCollaborativeStatesDetailsQuery();
  const { data: exhibitSignatures, isLoading: exhibitSignaturesLoading } = useProviderExhibitSignaturesQuery(me?.id);

  const matchByStateId = useMemo(
    () => matches?.reduce((map, match) => ({ ...map, [match.stateId]: match }), {} as Record<string, CollaborativePhysicianMatch>),
    [ matches ]
  );

  const stateById = useMemo(
    () => states.reduce((map, state) => ({ ...map, [state.id]: state }), {} as Record<string, State>),
    [ states ]
  );
  const collaborativeStateDetailsById = useMemo(
    () => collaborativeStateDetails.reduce((map, s) => ({ ...map, [s.stateId]: s }), {} as Record<string, CollaborativeStateDetails>),
    [ collaborativeStateDetails ]
  );
  const signedExhibitLicenseIds = useMemo(
    () => new Set(
      (exhibitSignatures ?? [])
        .filter(signature => !signature.archived && !CalendarDateService.date(signature.expireAt, 'YYYY-MM-DD').isBefore(CalendarDateService.date(), 'day'))
        .map(signature => signature.licenseId)
    ),
    [ exhibitSignatures ]
  );

  const rows = useMemo<PracticeStateRow[]>(() => {
    if (!licenses) return [];

    return licenses
      .filter(license => !!license.stateId)
      .map((license): PracticeStateRow => {
        const collaborativeStateDetails = collaborativeStateDetailsById[license.stateId];
        const state = stateById[license.stateId];
        const licenseCollaborativeAgreements = collaborativeAgreementsById?.[license.id] ?? [];
        const activeLicenseCollaborativeAgreement = licenseCollaborativeAgreements.find(agreement => agreement.archivedAt === null);
        const requiresCollaborativeAgreement = licenseRequiresCollaborativeAgreement(license, collaborativeStateDetails);
        const allowsStateExhibit = licenseAllowsStateExhibit(license, collaborativeStateDetails);
        const match = matchByStateId?.[license.stateId];
        const signedAgreement = match ? signedCollaborativeAgreementsByMatchId?.[match.id] : undefined;
        const matchRequested = matchRequestsByLicenseId?.[license.id]?.collaborativePhysicianMatchRequested ?? false;
        const status = deriveStatus(collaborativeStateDetails, license, activeLicenseCollaborativeAgreement, match, signedAgreement, matchRequested);
        const requiresStateExhibitAndCollaborativeAgreement = requiresCollaborativeAgreement && allowsStateExhibit;

        return {
          id: license.id ?? license.stateId,
          stateId: license.stateId,
          licenseId: license.id,
          providerId: license.userId,
          stateName: state?.fullName,
          requiresCollaborativeAgreement,
          allowsStateExhibit,
          activeCollaborativeAgreement: activeLicenseCollaborativeAgreement,
          licenseExpiration: license.expiration,
          providerGoLiveAllowed: license.providerGoLiveAllowed,
          status,
          requirementsSubtitle: requiresStateExhibitAndCollaborativeAgreement
            ? getRequirementsSubtitle(status, signedExhibitLicenseIds.has(license.id), collaborativeStateDetails?.authorizationRequirement ?? 'either')
            : undefined,
        };
      });
  }, [ licenses, stateById, collaborativeAgreementsById, matchByStateId, signedCollaborativeAgreementsByMatchId, matchRequestsByLicenseId, collaborativeStateDetailsById, signedExhibitLicenseIds, getRequirementsSubtitle ]);

  return { rows, isLoading: licensesLoading || statesLoading || collabAgreementsLoading || matchesLoading || signedCollaborativeAgreementsLoading || exhibitSignaturesLoading };
};
